import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2, Compass } from "lucide-react";
import { useMobi } from "@/lib/MobiContext";

/**
 * Mobi — the AI advisor avatar. Sits in the top-right header.
 *
 * Clicking the avatar opens a glowing chat panel anchored to the avatar.
 * Phase B will replace this placeholder image with a custom illustration / 3D
 * mascot once the user provides the asset; for now we render a CSS-only "glow orb"
 * that animates a gradient pulse — futuristic and ungendered.
 *
 * Backend integration: re-uses /api/ai/chat tutor endpoint (persona="advisor") so
 * the same AI service powers Mobi without any new route.
 */
export const MobiAvatar = () => {
  const [open, setOpen] = useState(false);
  const { ctx: pageCtx } = useMobi();
  const [messages, setMessages] = useState([
    { role: "mobi", text: "Hi — I'm Mobi, your AI advisor. Ask me to help build a quiz, plan a lesson, or anything else on the platform." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [messages, open]);

  // Allow any page (via MobiHint) to open this panel and optionally pre-seed a prompt
  useEffect(() => {
    const opener = (e) => {
      setOpen(true);
      const seed = e?.detail?.prompt;
      if (seed) setInput(seed);
    };
    window.addEventListener("open-mobi", opener);
    return () => window.removeEventListener("open-mobi", opener);
  }, []);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = input.trim();
    if (!t || sending) return;
    const next = [...messages, { role: "user", text: t }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", {
        persona: "advisor",
        message: t,
        history: next.slice(-6).map((m) => ({ role: m.role === "mobi" ? "assistant" : m.role, content: m.text })),
        context: pageCtx || undefined,
      });
      setMessages((cur) => [...cur, { role: "mobi", text: data?.reply || "(no reply)" }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mobi is sleeping — try again");
    } finally { setSending(false); }
  };

  return (
    <div className="relative">
      <button
        data-testid="mobi-avatar"
        onClick={() => setOpen((o) => !o)}
        title="Meet your advisor — Mobi"
        aria-label="Open Mobi advisor"
        className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-[#FFB400]/40 hover:ring-[#FFB400] transition-all hover:scale-105 group"
      >
        <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD600] via-[#FF8A00] to-[#7C3AED] animate-pulse" />
        <span className="absolute inset-[3px] rounded-full bg-[#0A0C10] flex items-center justify-center text-[#FFB400] font-bold text-sm tracking-tighter">M</span>
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
      </button>

      {open && (
        <>
          {/* Backdrop catches outside clicks */}
          <button aria-label="close" className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} />
          <div
            data-testid="mobi-panel"
            className="absolute right-0 top-12 z-50 w-[360px] sm:w-[400px] rounded-2xl overflow-hidden border border-[#FFB400]/40 shadow-[0_24px_64px_-16px_rgba(124,58,237,0.45),0_0_0_1px_rgba(255,180,0,0.18)] bg-[#0A0C10]"
            style={{
              backgroundImage: "radial-gradient(circle at top right, rgba(124,58,237,0.22), transparent 55%), radial-gradient(circle at bottom left, rgba(255,180,0,0.18), transparent 60%)",
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="relative w-9 h-9 rounded-full overflow-hidden ring-1 ring-[#FFB400]/50">
                  <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD600] via-[#FF8A00] to-[#7C3AED] animate-pulse" />
                  <span className="absolute inset-[2px] rounded-full bg-[#0A0C10] flex items-center justify-center text-[#FFB400] font-bold text-sm">M</span>
                </div>
                <div>
                  <div className="text-white font-semibold text-sm leading-tight">Mobi</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FFB400]">/ your AI advisor</div>
                </div>
              </div>
              <button data-testid="mobi-close" onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1"><X size={16} /></button>
            </div>

            {/* Page-aware context chip */}
            {pageCtx?.title && (
              <div className="px-4 py-1.5 border-b border-white/10 bg-[#7C3AED]/15 inline-flex items-center gap-1.5" data-testid="mobi-context-chip">
                <Compass size={11} className="text-[#A5B4FC]" />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#A5B4FC]">
                  on: <span className="text-white font-semibold normal-case tracking-normal">{pageCtx.title}</span>
                </span>
              </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} className="px-4 py-3 max-h-[360px] min-h-[220px] overflow-y-auto space-y-2.5 text-sm" data-testid="mobi-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-snug ${m.role === "user"
                      ? "bg-[#FFB400] text-[#0A0C10] rounded-br-sm"
                      : "bg-white/8 text-white rounded-bl-sm border border-white/10 backdrop-blur"}`}
                    style={m.role === "mobi" ? { boxShadow: "0 0 0 1px rgba(255,180,0,0.15), 0 8px 24px -8px rgba(124,58,237,0.25)" } : undefined}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 rounded-2xl bg-white/8 text-white/70 inline-flex items-center gap-2 border border-white/10">
                    <Loader2 size={12} className="animate-spin" /> Mobi is thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2 bg-black/30 backdrop-blur">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Mobi anything…"
                data-testid="mobi-input"
                className="flex-1 bg-white/8 border border-white/10 focus:border-[#FFB400] outline-none text-white text-sm rounded-full px-3.5 py-2 placeholder:text-white/40"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="mobi-send"
                className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FFB400] text-[#0A0C10] inline-flex items-center justify-center disabled:opacity-40"
              >
                <Send size={14} />
              </button>
            </form>

            {/* Quick prompts — dynamic based on context */}
            <div className="px-3 pb-3 flex flex-wrap gap-1.5" data-testid="mobi-quick-prompts">
              {(pageCtx?.suggestions?.length ? pageCtx.suggestions : ["How do I add a grade?", "Help me set up Math for Grade 9", "What's new this week?"]).map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white border border-white/10"
                >{q}</button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobiAvatar;
